import { useMutation } from '@tanstack/react-query';
import { RetailerType } from 'common/constants/entities';
import { productsApi } from 'common/services/api/products/products-api.service';
import {
  AttachImageFlagBody,
  EnsureProductBody,
  ExportProductsCsvBody,
  ExportProductTitlesBody,
  ExportSupplierOneFormatData,
  ExtractExternalDataBody,
  GetAIGenerationsProductsParams,
  GetAIGenerationsStatusParams,
  GetProductBySkuBody,
  PerformImageAnalysisBody,
  PostApproximateCostEstimateBody,
  PostCostEstimateBody,
  ResetValueBody,
  SaveImagesOrderParams,
  SaveScrapedAttributesData,
  SuggestCategoriesData,
  TargetProductsFilters,
} from 'common/services/api/products/products-api.types';
import { userService } from 'common/services/user.service';
import { ScopeId } from 'common/types/common';

export function useResetValueMutation() {
  const mutation = useMutation({
    mutationFn: async (filters: ResetValueBody) => {
      const retailer = userService.getAPIRetailer().toLowerCase();
      return productsApi.resetValue(filters, retailer);
    },
  });

  return mutation;
}

export function useExportTargetProductsCsv(args: ExportProductsCsvBody) {
  const mutation = useMutation({
    mutationFn: async () => {
      const retailer = userService.getAPIRetailer().toLowerCase();
      const { data } = await productsApi.exportTargetCsv(args, retailer);
      return data.id;
    },
  });

  return mutation;
}

export function useExportAmazonProducts(args: ExportProductsCsvBody) {
  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await productsApi.exportAmazonProducts(args);
      return data.id;
    },
  });

  return mutation;
}

export function usePerformOCRMutation() {
  const mutation = useMutation({
    mutationFn: async (pids: Array<string>) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = userService.ensureRetailer(RetailerType.Walmart)
        ? await productsApi.performWalmartOCR(pids)
        : await productsApi.performTargetOCR(retailer, pids);
      return data;
    },
  });

  return mutation;
}

export function usePerformBrandAnalysisMutation() {
  const mutation = useMutation({
    mutationFn: async (pid: string) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = userService.ensureRetailer(RetailerType.Walmart)
        ? await productsApi.performWalmartBrandAnalysis(pid)
        : await productsApi.performRetailerBrandAnalysis(retailer, pid);
      return data;
    },
  });

  return mutation;
}

export function usePerformImageAnalysisMutation() {
  const mutation = useMutation({
    mutationFn: async (params: { pid: string; body: PerformImageAnalysisBody }) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = userService.ensureRetailer(RetailerType.Walmart)
        ? await productsApi.performWalmartImageAnalysis(params.pid, params.body)
        : await productsApi.performRetailerImageAnalysis(retailer, params.pid, params.body);
      return data;
    },
  });

  return mutation;
}

export function useSuggestCategoryMutation() {
  const mutation = useMutation({
    mutationFn: async (data: SuggestCategoriesData) => {
      return data?.photos?.length ? productsApi.suggestCategoriesWithFiles(data) : productsApi.suggestCategories(data);
    },
  });

  return mutation;
}

export function useEnsureProductMutation() {
  const mutation = useMutation({
    mutationFn: async (body: EnsureProductBody) => {
      return productsApi.ensureProduct(body?.retailer, body);
    },
  });

  return mutation;
}

export function useDownloadCSVMutation() {
  const mutation = useMutation({
    mutationFn: async (body: { retailer: RetailerType; pid: string }) => {
      return productsApi.downloadCSV(body?.retailer, body?.pid);
    },
  });

  return mutation;
}

export function useSaveScrapedAttributesMutation() {
  const mutation = useMutation({
    mutationFn: async (params: SaveScrapedAttributesData) => {
      const { data } = await productsApi.saveScrapedAttributesData(
        userService.getAPIRetailer() as RetailerType,
        params
      );
      return data;
    },
  });

  return mutation;
}

export function useUpdateProductCategoryMutation() {
  const mutation = useMutation({
    mutationFn: async (params: { tcin: string; categoryId: string }) => {
      const { data } = await productsApi.updateProductCategory(
        userService.getAPIRetailer() as RetailerType,
        params?.tcin,
        params?.categoryId
      );
      return data;
    },
  });

  return mutation;
}

export function useImportProductsMutation() {
  const mutation = useMutation({
    mutationFn: async (args: TargetProductsFilters) => {
      const retailer = userService.getAPIRetailer() as RetailerType;
      const { data } = await productsApi.getProducts(retailer, args);
      return data;
    },
  });

  return mutation;
}

export function useAddImagesXlsxMutation() {
  const mutation = useMutation({
    mutationFn: async (files: File[]) => {
      const retailer = userService.getAPIRetailer() as RetailerType;
      return productsApi.addImagesXlsx(retailer, files);
    },
  });

  return mutation;
}

export function useDeleteProductMutation(body: { scope: ScopeId; id: string }) {
  const mutation = useMutation({
    mutationFn: async () => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = userService.ensureRetailer(RetailerType.Walmart)
        ? await productsApi.deleteWalmartProductById(body?.scope, body?.id)
        : await productsApi.deleteRetailerProductById(retailer, body?.scope, body?.id);
      return data;
    },
  });

  return mutation;
}

export function useSaveImagesOrder() {
  const mutation = useMutation({
    mutationFn: async (params: SaveImagesOrderParams) => {
      return productsApi.saveImagesOrder(params);
    },
  });

  return mutation;
}

export function useEstimateCostMutation() {
  const mutation = useMutation({
    mutationFn: async (body: PostCostEstimateBody) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = await productsApi.postEstimateCost(retailer, body);
      return data.totalCost;
    },
  });

  return mutation;
}

export function useEstimateApproximateCostMutation() {
  const mutation = useMutation({
    mutationFn: async (body: PostApproximateCostEstimateBody) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = await productsApi.postEstimateApproximateCost(retailer, body);
      return data.totalCost;
    },
  });

  return mutation;
}

export function useDownloadAIGenerationsCSVMutation(params: GetAIGenerationsProductsParams) {
  const mutation = useMutation({
    mutationFn: async () => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = userService.ensureRetailer(RetailerType.Walmart)
        ? await productsApi.getWalmartAIGenerationsCSV(params)
        : await productsApi.getAIGenerationsCSV(retailer, params);
      return data;
    },
  });

  return mutation;
}

export function useAttachImageFlagMutation() {
  const mutation = useMutation({
    mutationFn: async (body: AttachImageFlagBody) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      await productsApi.attachImageFlag(retailer, body);
    },
  });

  return mutation;
}

export function useDetachImageFlagMutation() {
  const mutation = useMutation({
    mutationFn: async (body: AttachImageFlagBody) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      await productsApi.detachImageFlag(retailer, body);
    },
  });

  return mutation;
}

export function useAIGenerationsStatusMutation(params: GetAIGenerationsStatusParams) {
  const mutation = useMutation({
    mutationFn: async () => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = userService.ensureRetailer(RetailerType.Walmart)
        ? await productsApi.getWalmartAiGenerationsStatus(params)
        : await productsApi.getAiGenerationsStatus(retailer, params);
      return data?.status;
    },
  });

  return mutation;
}

export function useExtractExternalDataMutation() {
  const mutation = useMutation({
    mutationFn: async (body: ExtractExternalDataBody) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      await productsApi.extractExternalData(retailer, body);
    },
  });

  return mutation;
}

export function useExtractProductExternalDataMutation(productID: string) {
  const mutation = useMutation({
    mutationFn: async () => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      await productsApi.extractProductExternalData(retailer, productID);
    },
  });

  return mutation;
}

export function useExportSupplierOneMutation() {
  const mutation = useMutation({
    mutationFn: async (body: ExportSupplierOneFormatData) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      await productsApi.exportSupplierOneFormat(retailer, body);
    },
  });

  return mutation;
}

export function useExportProductTitlesMutation(body: ExportProductTitlesBody) {
  const mutation = useMutation({
    mutationFn: async () => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = await productsApi.exportProductTitles(retailer, body);

      return data?.exportId;
    },
  });

  return mutation;
}

export function useGetProductBySkuMutation() {
  const mutation = useMutation({
    mutationFn: async (body: GetProductBySkuBody) => {
      const retailer = userService.getAPIRetailer() as RetailerType;

      const { data } = await productsApi.getProductBySku(retailer, body);

      return data?.tcin;
    },
  });

  return mutation;
}
